import React from 'react';
import MailEmailListPanel from './MailEmailListPanel';
import MailEmailDetailPanel from './MailEmailDetailPanel';
import MailTopBar from './MailTopBar';
import MailModals from './MailModals';
import { useMailHub } from './useMailHub';
import MailSettings from './MailSettings';
import { EMOJI_LIST, formatDateParis } from './mailHubUtils';
import {
  Mail, Plus, RefreshCw, User, ExternalLink, AlertCircle, CheckCircle, ArrowLeft, Settings, X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MailHub = ({ backendUrl, authToken, onNavigateToClient, onNavigateToCourtier, onNavigateToCompany, onNavigateToAssisteur, onNavigateToAssistanceClient, filterClientId = null, initialCompose = null, initialEmailId = null, initialFolder = 'inbox', onEmailOpened = null, onMinimize = null, hideHeader = false, onActionsReady = null }) => {
  const { t } = useTranslation('admin');
  const {
    accountStats, activeContactTab, addToSelectedLinks, attachmentToSave, attachments, bulkDelete, bulkMarkAsRead, bulkMarkAsSpam,
    bulkMarkAsUnread, bulkUnmarkSpam, cancelScheduledEmail, changeFolder, clientInfo, closeComposeModal, closePreviewModal, companiesList,
    companyContacts, composeForm, composeModalDragging, composeModalMinimized, composeModalPosition, composeModalRef, contactPickerField, contactResults,
    contactSearchQuery, courtierClients, createFolder, createQuickAnnuaireContact, createQuickClient, createQuickCourtier, creatingContact, currentFolder,
    currentPage, deleteEmail, deleteFolder, deselectAllCompanyContacts, deselectAllCourtierClients, deselectAllFromTab, downloadAttachment, dragOverFolderId,
    draggedEmailId, emailAccounts, emailFolders, emailToLink, emails, emailsPage, emailsPerPage, emptyFolder, errorMessage, moveEmailToFolder,
    filters, folderMenuOpen, folderToRename, formatDate, getSelectedCountByCategory, getUniqueCompanies, handleCRMAttachments, handleCRMLink,
    handleDragEnter, handleDragLeave, handleDragOver, handleDragStart, handleDrop, handleEmailDragEnd, handleFileSelect, handleFolderDragLeave,
    handleFolderDragOver, handleFolderDrop, handleImageInsert, hasActiveFilters, hasNextEmail, hasPreviousEmail, imageInputRef, insertEmoji,
    insertTable, isDragging, isEmailSelected, linkSearchQuery, linkSearchResults, linkSuggestions, loadEmails, loading,
    markAsSpam, navigateEmail, newFolderName, openAttachmentInNewTab, openCompanyContacts, openComposeInNewWindow, openComposeModal, openContactPicker,
    openCourtierClients, openRenameModal, openUnknownContactModal, paginateItems, previewAttachment, previewModal, processBulkEmails, processEmail,
    quickAnnuaireForm, quickClientForm, quickCourtierForm, quillFormats, quillModules, quillRef, removeAttachment,
    removeFromSelectedLinks, removeTrackingPixel, renameFolder, renameFolderName, renderPagination, renderPreviewContent, replyMode, resetFilters,
    saveLinkAndProcess, scheduledEmails, scheduledEmailsLoading, searchContacts, searchEntitiesForLink, searchQuery, selectAllCompanyContacts, selectAllCourtierClients,
    selectAllFromTab, selectContactEmail, selectEmail, setSelectedEmail, selectedAccount, selectedCompany, selectedCourtier, selectedEmail, selectedEmailIds,
    selectedFolderId, selectedLinks, sendEmail, setActiveContactTab, setAttachmentToSave, setComposeForm, setComposeModalDragOffset, setComposeModalDragging,
    setComposeModalMinimized, setContactSearchQuery, setCurrentFolder, setEmailToLink, setEmailsPage, setEmailsPerPage, setFolderMenuOpen, setFolderToRename,
    setLinkSearchQuery, setNewFolderName, setPreviewModal, setQuickAnnuaireForm, setQuickClientForm, setQuickCourtierForm, setRenameFolderName, setReplyMode,
    setSearchQuery, setSelectedAccount, setSelectedFolderId, setShowCRMAttachmentPicker, setShowCompanyContacts, setShowComposeModal, setShowContactPicker, setShowCourtierClients,
    setShowCreateFolderModal, setShowEmailLinkManager, setShowEmailMenu, setShowEmojiPicker, setShowFilters, setShowLinkModal, setShowRenameFolderModal, setShowSaveAttachmentModal,
    setShowTableDialog, setShowUnknownContactModal, setTableCols, setTableRows, setUnknownContactStep, setUnknownContactType, showCRMAttachmentPicker, showCompanyContacts,
    showComposeModal, showContactPicker, showCourtierClients, showCreateFolderModal, showEmailLinkManager, showEmailMenu, showEmojiPicker, showFilters,
    showLinkModal, showRenameFolderModal, showSaveAttachmentModal, showSuccess, showTableDialog, showUnknownContactModal, successMessage, syncEmails,
    syncing, tableCols, tableRows, toggleEmailSelection, toggleImportant, toggleSelectAll, trackingStats, unknownContactEmail,
    unknownContactStep, unknownContactType, unknownEmails, unmarkSpam, unreadCount, updateFilters,
    minimizedWindows, minimizeCurrentWindow, restoreMinimizedWindow, closeMinimizedWindow, updateMinimizedTabPosition
  } = useMailHub({ backendUrl, authToken, onNavigateToClient, onNavigateToCourtier, onNavigateToCompany, onNavigateToAssisteur, onNavigateToAssistanceClient, filterClientId, initialCompose, initialEmailId, initialFolder, onEmailOpened, onMinimize });

  const [showMailSettings, setShowMailSettings] = React.useState(false);

  // Remonte les actions vers le parent (HubCom) quand hideHeader=true
  React.useEffect(() => {
    if (!onActionsReady) return;
    onActionsReady({
      syncEmails,
      openComposeModal,
      setShowMailSettings,
      syncing,
      noAccount: emailAccounts.length === 0,
    });
  }, [onActionsReady, syncEmails, openComposeModal, setShowMailSettings, syncing, emailAccounts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="h-full flex flex-col">
      {/* Réglages Mail (rapatrié depuis Support Tools, temporaire) */}
      {showMailSettings && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50" onClick={() => setShowMailSettings(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">{t('mail.settingsTitle')}</h2>
              <button onClick={() => setShowMailSettings(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="mail-settings-close">
                <X size={20} />
              </button>
            </div>
            <div className="overflow-y-auto p-5">
              <MailSettings backendUrl={backendUrl} authToken={authToken} />
            </div>
          </div>
        </div>
      )}
      {/* Messages */}
      {successMessage && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center">
          <CheckCircle className="text-emerald-600 mr-3" size={20} />
          <span className="text-emerald-700">{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center">
          <AlertCircle className="text-red-600 mr-3" size={20} />
          <span className="text-red-700">{errorMessage}</span>
        </div>
      )}

      {/* Bannière filtre client */}
      {filterClientId && clientInfo && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <User className="text-blue-600" size={20} />
            <div>
              <p className="text-blue-900 font-semibold">
                {t('mail.clientEmailsBanner', { name: `${clientInfo.first_name} ${clientInfo.last_name}` })}
              </p>
              <p className="text-blue-700 text-sm">
                {t('mail.clientEmailsBannerDesc')}
              </p>
            </div>
          </div>
          <button
            onClick={() => window.history.back()}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1 text-sm"
          >
            <ArrowLeft size={14} />
            <span>{t('mail.back')}</span>
          </button>
        </div>
      )}

      {/* Header — masqué quand intégré dans HubCom (hideHeader=true) */}
      {!hideHeader && (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center space-x-4">
            <Mail className="text-indigo-600 shrink-0" size={32} />
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">📧 {t('mail.title')}</h2>
              <p className="text-slate-600 text-sm">
                {filterClientId ? t('mail.subtitleClient') : t('mail.subtitleDefault')}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="mail-settings-btn"
              onClick={() => setShowMailSettings(true)}
              className="h-10 w-10 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors flex items-center justify-center"
              title={t('mail.settings')}
            >
              <Settings size={18} />
            </button>
            <button
              onClick={() => window.open('/mail-window', '_blank')}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors flex items-center space-x-2"
              title={t('mail.openNewTab')}
            >
              <ExternalLink size={16} />
              <span className="hidden sm:inline">{t('mail.newTab')}</span>
            </button>

            <button
              data-testid="mail-sync-btn"
              onClick={syncEmails}
              disabled={syncing || !selectedAccount}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
            >
              <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">{syncing ? t('mail.syncing') : t('mail.sync')}</span>
            </button>

            <button
              data-testid="mail-compose-btn"
              onClick={() => openComposeModal()}
              disabled={!selectedAccount}
              className="px-4 sm:px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
            >
              <Plus size={16} />
              <span>{t('mail.newMessage')}</span>
            </button>
          </div>
        </div>
      </div>
      )}

      {/* No Account Warning */}
      {emailAccounts.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <AlertCircle className="text-amber-600 mx-auto mb-3" size={48} />
          <h3 className="text-lg font-bold text-slate-900 mb-2">{t('mail.noAccount')}</h3>
          <p className="text-slate-600 mb-4">
            {t('mail.noAccountHelp')}
          </p>
        </div>
      )}

      {/* Top Navigation Bar */}
      {emailAccounts.length > 0 && (
        <>
          <MailTopBar
            accountStats={accountStats}
            changeFolder={changeFolder}
            currentFolder={currentFolder}
            deleteFolder={deleteFolder}
            dragOverFolderId={dragOverFolderId}
            emailAccounts={emailAccounts}
            emailFolders={emailFolders}
            folderMenuOpen={folderMenuOpen}
            handleFolderDragLeave={handleFolderDragLeave}
            handleFolderDragOver={handleFolderDragOver}
            handleFolderDrop={handleFolderDrop}
            openRenameModal={openRenameModal}
            processBulkEmails={processBulkEmails}
            selectedAccount={selectedAccount}
            selectedFolderId={selectedFolderId}
            setCurrentFolder={setCurrentFolder}
            setFolderMenuOpen={setFolderMenuOpen}
            setSelectedAccount={setSelectedAccount}
            setSelectedFolderId={setSelectedFolderId}
            setShowCreateFolderModal={setShowCreateFolderModal}
            unreadCount={unreadCount}
          />

          {/* Main Layout (2 Columns → master-détail responsive) */}
          <div
            className="grid grid-cols-1 lg:grid-cols-12 gap-6 flex-1 min-h-0 lg:grid-rows-1 lg:overflow-hidden"
          >
            {/* Email List (Column 1) */}
            <MailEmailListPanel
              accountStats={accountStats}
              bulkDelete={bulkDelete}
              bulkMarkAsRead={bulkMarkAsRead}
              bulkMarkAsSpam={bulkMarkAsSpam}
              bulkMarkAsUnread={bulkMarkAsUnread}
              bulkUnmarkSpam={bulkUnmarkSpam}
              cancelScheduledEmail={cancelScheduledEmail}
              currentFolder={currentFolder}
              deleteEmail={deleteEmail}
              draggedEmailId={draggedEmailId}
              emailFolders={emailFolders}
              moveEmailToFolder={moveEmailToFolder}
              emails={emails}
              emailsPage={emailsPage}
              emailsPerPage={emailsPerPage}
              emptyFolder={emptyFolder}
              filters={filters}
              formatDate={formatDate}
              formatDateParis={formatDateParis}
              handleCRMLink={handleCRMLink}
              handleDragStart={handleDragStart}
              handleEmailDragEnd={handleEmailDragEnd}
              hasActiveFilters={hasActiveFilters}
              loading={loading}
              markAsSpam={markAsSpam}
              openUnknownContactModal={openUnknownContactModal}
              resetFilters={resetFilters}
              scheduledEmails={scheduledEmails}
              scheduledEmailsLoading={scheduledEmailsLoading}
              searchQuery={searchQuery}
              selectEmail={selectEmail}
              selectedEmail={selectedEmail}
              selectedEmailIds={selectedEmailIds}
              setComposeForm={setComposeForm}
              setEmailToLink={setEmailToLink}
              setEmailsPage={setEmailsPage}
              setEmailsPerPage={setEmailsPerPage}
              setSearchQuery={setSearchQuery}
              setShowComposeModal={setShowComposeModal}
              setShowEmailLinkManager={setShowEmailLinkManager}
              setShowFilters={setShowFilters}
              showFilters={showFilters}
              toggleEmailSelection={toggleEmailSelection}
              toggleImportant={toggleImportant}
              toggleSelectAll={toggleSelectAll}
              unknownEmails={unknownEmails}
              unmarkSpam={unmarkSpam}
              updateFilters={updateFilters}
            />

          {/* Email Detail (Column 2) */}
            <MailEmailDetailPanel
              onBackToList={() => setSelectedEmail(null)}
              currentFolder={currentFolder}
              deleteEmail={deleteEmail}
              emailFolders={emailFolders}
              moveEmailToFolder={moveEmailToFolder}
              downloadAttachment={downloadAttachment}
              formatDateParis={formatDateParis}
              handleCRMLink={handleCRMLink}
              hasNextEmail={hasNextEmail}
              hasPreviousEmail={hasPreviousEmail}
              markAsSpam={markAsSpam}
              navigateEmail={navigateEmail}
              openAttachmentInNewTab={openAttachmentInNewTab}
              openComposeModal={openComposeModal}
              previewAttachment={previewAttachment}
              processEmail={processEmail}
              removeTrackingPixel={removeTrackingPixel}
              selectedEmail={selectedEmail}
              setAttachmentToSave={setAttachmentToSave}
              setComposeForm={setComposeForm}
              setEmailToLink={setEmailToLink}
              setReplyMode={setReplyMode}
              setShowComposeModal={setShowComposeModal}
              setShowEmailLinkManager={setShowEmailLinkManager}
              setShowEmailMenu={setShowEmailMenu}
              setShowSaveAttachmentModal={setShowSaveAttachmentModal}
              showEmailMenu={showEmailMenu}
              toggleImportant={toggleImportant}
              trackingStats={trackingStats}
              unmarkSpam={unmarkSpam}
            />
        </div>
        </>
      )}

      <MailModals
        commonProps={{
          EMOJI_LIST, authToken, backendUrl, emailAccounts, selectedAccount, setSelectedAccount, showSuccess, loadEmails
        }}
        composeProps={{
          attachments, closeComposeModal, composeForm, composeModalDragging, composeModalMinimized, composeModalPosition,
          composeModalRef, handleDragEnter, handleDragLeave, handleDragOver, handleDrop, handleFileSelect,
          handleImageInsert, imageInputRef, insertEmoji, insertTable, isDragging, openComposeInNewWindow,
          openContactPicker, quillFormats, quillModules, quillRef, removeAttachment, replyMode,
          sendEmail, setComposeForm, setComposeModalDragOffset, setComposeModalDragging, setComposeModalMinimized,
          setShowEmojiPicker, setShowTableDialog, setTableCols, setTableRows, showComposeModal, showEmojiPicker,
          showTableDialog, tableCols, tableRows,
          minimizedWindows, minimizeCurrentWindow, restoreMinimizedWindow, closeMinimizedWindow, updateMinimizedTabPosition
        }}
        contactsProps={{
          activeContactTab, addToSelectedLinks, companiesList, companyContacts, contactPickerField, contactResults,
          contactSearchQuery, courtierClients, createQuickAnnuaireContact, createQuickClient, createQuickCourtier, creatingContact,
          currentPage, deselectAllCompanyContacts, deselectAllCourtierClients, deselectAllFromTab, getSelectedCountByCategory, getUniqueCompanies,
          isEmailSelected, linkSearchQuery, linkSearchResults, linkSuggestions, openCompanyContacts, openCourtierClients,
          paginateItems, quickAnnuaireForm, quickClientForm, quickCourtierForm, removeFromSelectedLinks, renderPagination,
          saveLinkAndProcess, searchContacts, searchEntitiesForLink, selectAllCompanyContacts, selectAllCourtierClients, selectAllFromTab,
          selectContactEmail, selectedCompany, selectedCourtier, selectedLinks, setActiveContactTab, setContactSearchQuery,
          setLinkSearchQuery, setQuickAnnuaireForm, setQuickClientForm, setQuickCourtierForm, setShowCompanyContacts, setShowContactPicker,
          setShowCourtierClients, setShowLinkModal, setShowUnknownContactModal, setUnknownContactStep, setUnknownContactType, showCompanyContacts,
          showContactPicker, showCourtierClients, showLinkModal, showUnknownContactModal, unknownContactEmail, unknownContactStep,
          unknownContactType
        }}
        folderProps={{
          createFolder, folderToRename, newFolderName, renameFolder, renameFolderName, setFolderToRename,
          setNewFolderName, setRenameFolderName, setShowCreateFolderModal, setShowRenameFolderModal, showCreateFolderModal, showRenameFolderModal
        }}
        attachmentProps={{
          attachmentToSave, closePreviewModal, emailToLink, handleCRMAttachments, previewModal, renderPreviewContent,
          setAttachmentToSave, setEmailToLink, setPreviewModal, setSelectedEmail, setShowCRMAttachmentPicker, setShowEmailLinkManager, setShowSaveAttachmentModal,
          showCRMAttachmentPicker, showEmailLinkManager, showSaveAttachmentModal
        }}
      />
    </div>
  );
};

export default MailHub;
